import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOnboardingStatus } from "@/hooks";
import { CompleteProfileStep } from "./steps/complete-profile-step";
import { useQueryClient } from "@tanstack/react-query";
import { useUserMemberships } from "@/hooks/company-membership/use-company-membership";
import { clearProgress } from "@/utils/create-company-progress";

export default function OnboardingIndexPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: onboardingStatus, isLoading } = useOnboardingStatus();
  const { data: memberships } = useUserMemberships();

  // Redirect if profile is already complete
  // Invited users still need to complete profile, but skip company creation after
  useEffect(() => {
    if (onboardingStatus) {
      // If profile is already complete, redirect based on whether user has companies
      if (onboardingStatus.profileComplete) {
        const hasCompanies = (memberships?.length ?? 0) > 0;
        if (hasCompanies || onboardingStatus.wasInvited) {
          // User has companies or was invited - go to home (skip company creation)
          navigate("/");
        } else {
          // User has no companies - redirect to create company
          navigate("/create-company");
        }
      }
    }
  }, [onboardingStatus, memberships, navigate]);

  const handleProfileComplete = async () => {
    // Clear any stored company creation progress to ensure fresh start
    clearProgress();
    
    // Invalidate onboarding status to get updated state
    await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] });
    await queryClient.invalidateQueries({ queryKey: ["userMemberships"] });
    
    // Refetch memberships to check if user has companies
    const updatedMemberships = await queryClient.fetchQuery({
      queryKey: ["userMemberships"],
      queryFn: async () => {
        const { companyMembershipService } = await import('@/services/company-membership/company-membership.service');
        return companyMembershipService.getUserMemberships();
      },
    });

    // Redirect based on whether user has companies or was invited
    // Refetch onboarding status to check if user was invited
    const updatedOnboardingStatus = await queryClient.fetchQuery({
      queryKey: ["onboardingStatus"],
      queryFn: async () => {
        const { authService } = await import('@/services/auth');
        return authService.getOnboardingStatus();
      },
    });

    const hasCompanies = (updatedMemberships?.length ?? 0) > 0;
    if (hasCompanies || updatedOnboardingStatus?.wasInvited) {
      // User has companies or was invited - go to home (skip company creation)
      navigate("/");
    } else {
      // User has no companies - redirect to create company
      navigate("/create-company");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // If profile is already complete, show loading while redirect happens
  if (onboardingStatus?.profileComplete) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <CompleteProfileStep onComplete={handleProfileComplete} />
        </div>
      </div>
    </div>
  );
}

